import { isSupportedConvertImageFile } from '../convert/convertImageEngine';
import { encodeCanvas } from '../shared/imageOutputFormats';

export { formatFileSize } from '../compress/compressImageEngine';
export const isSupportedUpscaleImageFile = isSupportedConvertImageFile;

export const MAX_FILE_BYTES = 30 * 1024 * 1024;

export const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

// Ceiling on the *result*, not the source: 30 MP of RGBA is ~120 MB in the
// worker plus the same again on the canvas, which is as far as a phone browser
// can be pushed before the tab is killed.
export const MAX_OUTPUT_PIXELS = 30_000_000;

export const SCALE_OPTIONS = [2, 4];

// Both models are dynamic-shape NCHW float32 RGB in 0..1, x4 output.
const SPAN_URL = import.meta.env.VITE_UPSCALE_SPAN_MODEL_URL
    || 'https://huggingface.co/facefusion/models-3.0.0/resolve/main/span_kendata_x4.onnx';
const ESRGAN_URL = import.meta.env.VITE_UPSCALE_ESRGAN_MODEL_URL
    || 'https://huggingface.co/facefusion/models-3.0.0/resolve/main/real_esrgan_x4.onnx';

export const ENGINES = {
    // SPAN: ~2 MB and ~20 s per input megapixel even on the WASM backend, so it
    // still works on machines whose WebGPU cannot run it.
    ai: { id: 'ai', ai: true, modelUrl: SPAN_URL, modelScale: 4, downloadMb: 2, requiresWebgpu: false },
    // Real-ESRGAN: ~66 MB and roughly 40× the compute of SPAN — fine on a GPU,
    // hopeless on WASM, so this one is GPU-only and says so when it cannot run.
    aiMax: { id: 'aiMax', ai: true, modelUrl: ESRGAN_URL, modelScale: 4, downloadMb: 66, requiresWebgpu: true },
};

export const ENGINE_IDS = Object.keys(ENGINES);

// Ids shared with every other image tool's picker. `original` is the old
// "auto": transparency stays lossless, a photo stays in a photo codec.
export const FORMAT_OPTIONS = ['original', 'png', 'jpg', 'webp', 'avif'];

async function prepareFile(file) {
    const name = (file.name || '').toLowerCase();
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (!isHeic) return file;

    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const base = file.name.replace(/\.(heic|heif)$/i, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

async function decodeToImageData(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('decode failed'));
            el.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        return ctx.getImageData(0, 0, canvas.width, canvas.height);
    } finally {
        URL.revokeObjectURL(url);
    }
}

export async function readImageDimensions(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => reject(new Error('decode failed'));
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** Largest source dimension pair that still fits MAX_OUTPUT_PIXELS at `scale`. */
export function fitsOutputLimit(width, height, scale) {
    return width * scale * height * scale <= MAX_OUTPUT_PIXELS;
}

function outputMeta(file, format, hasAlpha) {
    if (format === 'png') return { mime: 'image/png', ext: 'png', quality: null };
    if (format === 'jpeg' || format === 'jpg') return { mime: 'image/jpeg', ext: 'jpg', quality: 0.92 };
    if (format === 'webp') return { mime: 'image/webp', ext: 'webp', quality: 0.92 };
    if (format === 'avif') return { mime: 'image/avif', ext: 'avif', quality: 0.92 };

    // auto — keep transparency lossless, otherwise stay with a photo codec so a
    // 4× enlargement does not turn a 2 MB JPEG into a 40 MB PNG.
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    const isPngLike = hasAlpha || type === 'image/png' || name.endsWith('.png');
    if (isPngLike) return { mime: 'image/png', ext: 'png', quality: null };
    if (type === 'image/webp' || name.endsWith('.webp')) return { mime: 'image/webp', ext: 'webp', quality: 0.92 };
    return { mime: 'image/jpeg', ext: 'jpg', quality: 0.92 };
}

export function upscaledName(file, scale, ext) {
    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    return `${base}-${scale}x.${ext}`;
}

function rgbaHasAlpha(data) {
    for (let p = 3; p < data.length; p += 4) if (data[p] !== 255) return true;
    return false;
}

function runWorker(imageData, { engine, scale }, onProgress) {
    return new Promise((resolve, reject) => {
        const worker = new Worker(new URL('./upscaleWorker.js', import.meta.url), { type: 'module' });
        const cfg = ENGINES[engine] || ENGINES.ai;

        worker.onmessage = (e) => {
            const { type, stage, pct, rgba, width, height, message } = e.data;
            if (type === 'progress') {
                onProgress?.({ stage, pct });
            } else if (type === 'done') {
                worker.terminate();
                resolve({ rgba: new Uint8ClampedArray(rgba), width, height });
            } else if (type === 'error') {
                worker.terminate();
                const err = new Error(message || 'upscale failed');
                if (message === 'NO_WEBGPU') err.code = 'NO_WEBGPU';
                reject(err);
            }
        };
        worker.onerror = (err) => {
            worker.terminate();
            reject(err instanceof Error ? err : new Error('upscale failed'));
        };

        // The pixel buffer is transferred, so `imageData` must not be reused
        // after this call — the caller re-decodes per run.
        const buffer = imageData.data.buffer;
        worker.postMessage({
            rgba: buffer,
            width: imageData.width,
            height: imageData.height,
            engine: cfg.id,
            scale,
            modelUrl: cfg.modelUrl,
            modelScale: cfg.modelScale,
            requiresWebgpu: cfg.requiresWebgpu,
        }, [buffer]);
    });
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
            mime,
            quality ?? undefined,
        );
    });
}

/**
 * Upscale one file end to end: decode → worker → encode.
 *
 * @returns {Promise<{name, blob, originalSize, upscaledSize, originalWidth,
 *   originalHeight, width, height, scale, engine}>}
 */
export async function upscaleImageFile(file, { engine = 'ai', scale = 2, format = 'auto' }, onProgress) {
    onProgress?.({ stage: 'decode', pct: 0 });
    const imageData = await decodeToImageData(file);
    const originalWidth = imageData.width;
    const originalHeight = imageData.height;

    if (!fitsOutputLimit(originalWidth, originalHeight, scale)) {
        const err = new Error('output too large');
        err.code = 'TOO_LARGE';
        throw err;
    }

    const hasAlpha = rgbaHasAlpha(imageData.data);
    const meta = outputMeta(file, format, hasAlpha);

    const result = await runWorker(imageData, { engine, scale }, onProgress);

    onProgress?.({ stage: 'encode', pct: 0 });
    const canvas = document.createElement('canvas');
    canvas.width = result.width;
    canvas.height = result.height;
    const ctx = canvas.getContext('2d');
    if (meta.mime === 'image/jpeg') {
        // JPEG has no alpha — flatten onto white instead of letting the encoder
        // turn transparent pixels black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const out = new ImageData(result.rgba, result.width, result.height);
    if (meta.mime === 'image/jpeg') {
        const tmp = document.createElement('canvas');
        tmp.width = result.width;
        tmp.height = result.height;
        tmp.getContext('2d').putImageData(out, 0, 0);
        ctx.drawImage(tmp, 0, 0);
    } else {
        ctx.putImageData(out, 0, 0);
    }

    // Shared encoder: AVIF is written in WebAssembly where the canvas cannot.
    const formatId = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/avif': 'avif' }[meta.mime];
    const { blob, ext } = await encodeCanvas(canvas, formatId, { quality: meta.quality ?? 0.92 });

    return {
        name: upscaledName(file, scale, ext),
        blob,
        originalSize: file.size,
        upscaledSize: blob.size,
        // Named to match what ImageGridPreview's "resize" variant reads.
        originalWidth,
        originalHeight,
        width: result.width,
        height: result.height,
        scale,
        engine,
    };
}
